"use client";

import * as React from "react";
import { useParams, usePathname, useRouter } from "next/navigation";
import { CommandPalette } from "./command-palette";
import { useHotkey } from "@/lib/hooks/use-hotkey";
import { useUIStore } from "@/lib/stores/ui";
import { useViewer3DStore } from "@/lib/stores/viewer-3d";
import { toggleHistoryOverrideAction } from "@/lib/actions/feature-flag-actions";
import { ReportHistoryEnabledContext } from "./history-enabled-report";
import { useFeedbackEnabled } from "@/components/providers/feedback-config-provider";
import { debugToolsEnabled } from "@/lib/environment";

export type AppContext =
  | { level: "global" }
  | { level: "project"; projectId: string }
  | { level: "part"; projectId: string; partId: string }
  | { level: "procedure"; projectId: string; partId: string; procedureId: string }
  | {
      level: "operation";
      projectId: string;
      partId: string;
      procedureId: string;
      operationId: string;
    };

function useAppContext(): AppContext {
  const params = useParams();
  const pathname = usePathname();

  const projectId = typeof params?.projectId === "string" ? params.projectId : undefined;

  const partId = React.useMemo(
    () => pathname.match(/\/parts\/([^/]+)/)?.[1] ?? undefined,
    [pathname]
  );

  const procedureId = React.useMemo(() => {
    if (!projectId || !partId) return undefined;
    const prefix = `/projects/${projectId}/parts/${partId}/procedures/`;
    if (!pathname.startsWith(prefix)) return undefined;
    const segment = pathname.slice(prefix.length).split("/")[0];
    if (!segment || segment === "undefined") return undefined;
    return segment;
  }, [pathname, projectId, partId]);

  const operationId = React.useMemo(() => {
    if (!projectId || !partId || !procedureId) return undefined;
    const prefix = `/projects/${projectId}/parts/${partId}/procedures/${procedureId}/operations/`;
    if (!pathname.startsWith(prefix)) return undefined;
    const segment = pathname.slice(prefix.length).split("/")[0];
    if (!segment || segment === "undefined") return undefined;
    return segment;
  }, [pathname, projectId, partId, procedureId]);

  if (operationId && procedureId && partId && projectId) {
    return { level: "operation", projectId, partId, procedureId, operationId };
  }
  if (procedureId && partId && projectId) {
    return { level: "procedure", projectId, partId, procedureId };
  }
  if (partId && projectId) {
    return { level: "part", projectId, partId };
  }
  if (projectId) {
    return { level: "project", projectId };
  }
  return { level: "global" };
}

export function CommandPaletteProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false);
  const router = useRouter();
  const pathname = usePathname();
  const context = useAppContext();
  const setSettingsOpen = useUIStore((s) => s.setSettingsOpen);
  const setHotkeyHelpOpen = useUIStore((s) => s.setHotkeyHelpOpen);
  const setFeedbackOpen = useUIStore((s) => s.setFeedbackOpen);
  const debugOpen = useUIStore((s) => s.debugOpen);
  const setDebugOpen = useUIStore((s) => s.setDebugOpen);
  const feedbackEnabled = useFeedbackEnabled();
  const [historyEnabled, setHistoryEnabled] = React.useState<boolean | null>(null);
  const toggleHistory = React.useCallback(() => {
    void toggleHistoryOverrideAction().then(() => router.refresh());
  }, [router]);
  const requestCameraReset = useViewer3DStore((s) => s.requestCameraReset);
  const requestFrameObject = useViewer3DStore((s) => s.requestFrameObject);
  const setFlyMode = useViewer3DStore((s) => s.setFlyMode);
  const orthographic = useViewer3DStore((s) => s.orthographic);
  const setOrthographic = useViewer3DStore((s) => s.setOrthographic);
  const spookyMode = useViewer3DStore((s) => s.spookyMode);
  const setSpookyMode = useViewer3DStore((s) => s.setSpookyMode);

  const handleAction = React.useCallback((action: () => void) => {
    action();
    setOpen(false);
  }, []);

  const handleNavigate = React.useCallback(
    (url: string) => {
      router.push(url);
      setOpen(false);
    },
    [router]
  );

  const toggle = React.useCallback(() => setOpen((o) => !o), []);

  useHotkey("ctrl+k,meta+k,ctrl+shift+p,meta+shift+p", toggle, {
    description: "Open command palette",
    preventDefault: true,
    enableOnFormTags: true,
  });

  useHotkey(
    "c",
    () => {
      if (context.level === "procedure" || context.level === "operation") {
        router.push(
          `/projects/${context.projectId}/parts/${context.partId}/procedures/${context.procedureId}/create-operation`
        );
      } else if (context.level === "part") {
        router.push(`/projects/${context.projectId}/parts/${context.partId}/create-procedure`);
      }
    },
    { description: "Create", preventDefault: false, enableOnFormTags: false }
  );

  useHotkey("h", () => requestCameraReset(), {
    description: "Reset view to home",
    preventDefault: false,
    enableOnFormTags: false,
  });

  useHotkey("f", () => requestFrameObject(), {
    description: "Frame the part",
    preventDefault: false,
    enableOnFormTags: false,
  });

  useHotkey(
    "shift+backquote",
    () => {
      // Enter fly mode; the in-viewer controller seeds the camera and acquires
      // pointer lock. Guard against key-repeat re-entry.
      if (!useViewer3DStore.getState().flyMode) setFlyMode(true);
    },
    { description: "Fly mode (3D viewer)", preventDefault: true, enableOnFormTags: false }
  );

  return (
    <ReportHistoryEnabledContext.Provider value={setHistoryEnabled}>
      {children}
      <CommandPalette
        open={open}
        onOpenChange={setOpen}
        context={context}
        pathname={pathname}
        onNavigate={handleNavigate}
        onAction={handleAction}
        onOpenSettings={() => handleAction(() => setSettingsOpen(true))}
        onOpenHotkeyHelp={() => handleAction(() => setHotkeyHelpOpen(true))}
        onOpenFeedback={() => handleAction(() => setFeedbackOpen(true))}
        feedbackEnabled={feedbackEnabled}
        onResetView={() => handleAction(requestCameraReset)}
        onToggleOrthographic={() => handleAction(() => setOrthographic(!orthographic))}
        orthographic={orthographic}
        onToggleSpookyMode={() => handleAction(() => setSpookyMode(!spookyMode))}
        spookyMode={spookyMode}
        onToggleHistory={() => handleAction(toggleHistory)}
        historyEnabled={historyEnabled}
        onToggleDebug={() => handleAction(() => setDebugOpen(!debugOpen))}
        debugOpen={debugOpen}
        debugAvailable={debugToolsEnabled()}
      />
    </ReportHistoryEnabledContext.Provider>
  );
}
